import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronRight } from 'lucide-react';

const CategoryDropdown = ({
  categoriesList,
  isCategory,
  selectedCategory,
  setSelectedCategory,
  excludeCategoryName,
  disabled = false
}) => {
  const [hierarchicalOptions, setHierarchicalOptions] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [excludedIds, setExcludedIds] = useState([]);
  const catmoNodeId = useSelector(state => state.docCategoryReducer.catmoNodeId);

  // Toggle expanded state for a category
  const toggleCategory = (categoryId, e) => {
    if (disabled) return; // Don't allow toggling if disabled
    e.stopPropagation();
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Check if a category has children
  const hasChildren = (category) => {
    return category.categories && category.categories.length > 0;
  };


  // Function to check if a category is the catmo node or its descendant
  const isCatmoRelated = (category) => {
    // Check if this is the catmo node itself
    if (category.name && category.name.toLowerCase() === 'catmo') {
      // console.log(`Excluding catmo node: ${category.name} (ID: ${category.id})`);
      return true;
    }
    
    // Check if this category's ID matches the catmoNodeId from Redux
    if (catmoNodeId && category.id === catmoNodeId) {
      // console.log(`Excluding catmo node by ID: ${category.id}`);
      return true;
    }
    
    // Check if this category's parent is the catmo node
    if (catmoNodeId && 
        (category.parent === catmoNodeId || 
         (typeof category.parent === 'object' && category.parent?.id === catmoNodeId))) {
      // console.log(`Excluding catmo child: ${category.name} (parent ID: ${catmoNodeId})`);
      return true;
    }
    
    return false;
  };

  // Function to collect all IDs that should be excluded (catmo and descendants)
  const collectExcludedIds = (categories, parentIsCatmo = false) => {
    if (!categories || !Array.isArray(categories)) return [];
    
    let excludedIds = [];
    
    for (const category of categories) {
      // Check if this category is catmo-related
      const isCatmo = isCatmoRelated(category);
      
      // If this category is catmo-related or its parent is catmo-related
      if (isCatmo || parentIsCatmo) {
        excludedIds.push(category.id);
        
        // All descendants of a catmo category should also be excluded
        if (category.categories && category.categories.length > 0) {
          const childIds = collectExcludedIds(category.categories, true);
          excludedIds = [...excludedIds, ...childIds];
        }
      } else {
        // For non-catmo categories, check their children
        if (category.categories && category.categories.length > 0) {
          const childIds = collectExcludedIds(category.categories, false);
          excludedIds = [...excludedIds, ...childIds];
        }
      }
    }
    
    return excludedIds;
  };
  

  // Process categories with exclusion
  useEffect(() => {
    if (categoriesList?.categories?.length) {
      // console.log("Building dropdown with exclude name:", excludeCategoryName);
      
      // Collect IDs to exclude if we're supposed to exclude catmo categories
      let ids = [];
      if (excludeCategoryName && excludeCategoryName.toLowerCase() === 'catmo') {
        ids = collectExcludedIds(categoriesList.categories);
        // console.log("IDs to exclude:", ids);
        setExcludedIds(ids);
      }
      
      // Build hierarchical options with exclusions
      const categoryArrayForDisplay = (categories, level = 0) => {
        if (!categories || !Array.isArray(categories)) return [];
        
        let result = [];
        
        for (const category of categories) {
          // Skip if this category's ID is in the excluded list
          if (ids.includes(category.id)) {
            // console.log(`Excluding category: ${category.name} (ID: ${category.id})`);
            continue;
          }
          
          result.push({
            id: category.id,
            name: category.name,
            level,
            parent: category.parent,
            hasChildren: hasChildren(category)
          });
          
          // Recursively add subcategories if they exist and are expanded
          if (hasChildren(category) && expandedCategories[category.id]) {
            const childOptions = categoryArrayForDisplay(category.categories, level + 1);
            result = [...result, ...childOptions];
          }
        }
        
        return result;
      };
      
      // Process categories
      const options = categoryArrayForDisplay(categoriesList.categories);
      // console.log("Final dropdown options:", options);
      setHierarchicalOptions(options);
    }
  }, [categoriesList, expandedCategories, excludeCategoryName, catmoNodeId]);



  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // const handleSelectCategory = (category) => {
  //   setSelectedCategory({
  //     id: category.id,
  //     name: category.name,
  //     parent: category.parent
  //   });
  //   setIsOpen(false);
  // };

//   const handleSelectCategory = (category, e) => {
//   // Stop any event propagation
//   if (e) {
//     e.stopPropagation();
//   }

//   setSelectedCategory({
//     id: category.id,
//     name: category.name,
//     parent: category.parent
//   });
  
//   setIsOpen(false);
// };

const handleSelectCategory = (category) => {
  if (disabled) return; // Don't allow selection if disabled
  // console.log("CategoryDropdown - selecting category:", category);
  
  // Create a clean category object to send back
  const selectedCat = {
    id: category.id,
    name: category.name,
    parent: category.parent
  };
  
  // Call the parent's setSelectedCategory function
  setSelectedCategory(selectedCat);
  setIsOpen(false);
};

  return (
    <div ref={dropdownRef} className="select-dropdown-wrapper" style={{ position: 'relative', width: '100%' }}>
      {/* Custom dropdown button that mimics the original select style */}
      <div 
        className="form-select" 
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          width: '70%',
          background: disabled ? '#f8f9fa' : 'white',
          border: '1px solid #ced4da',
          borderRadius: '0.25rem',
          padding: '0.375rem 0.75rem',
          fontSize: '14px',
          lineHeight: '1.5',
          opacity: disabled ? 0.6 : 1
        }}
      >
        <span>{selectedCategory?.name || 'Select a category'}</span>
        <svg 
          width="10" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          style={{ transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      
      {/* Dropdown menu */}
      {isOpen && !disabled && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 10,
            width: '100%',
            marginTop: '2px',
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: '0.25rem',
            boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
            maxHeight: '300px',
            overflowY: 'auto'
          }}
        >
          {/* Default "Select a category" option */}
          <div 
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderBottom: '1px solid #f0f0f0'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            onClick={() => {
              if (disabled) return;
              setSelectedCategory(null);
              setIsOpen(false);
            }}
          >
            Select a category
          </div>
          
          {/* Category options */}
          {hierarchicalOptions.map(option => (
            <div 
              key={option.id} 
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: selectedCategory?.id === option.id ? '#f0f0f0' : 'white',
                display: 'flex',
                alignItems: 'center'
              }}
              onClick={(e) => {
              // Only select if it's a click on the row, not on the expand/collapse icon, and not disabled
              if (!e.defaultPrevented && !disabled) {
                handleSelectCategory(option, e);
              }
            }}
              onMouseEnter={(e) => {
                if (selectedCategory?.id !== option.id) {
                  e.target.style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCategory?.id !== option.id) {
                  e.target.style.backgroundColor = 'white';
                }
              }}
            >
              {/* Indentation based on level */}
              <span style={{ width: `${option.level * 16}px`, display: 'inline-block' }}></span>
              
              {/* Chevron icon for expandable categories */}
              {option.hasChildren && (
                <span 
                  style={{ marginRight: '4px', cursor: disabled ? 'not-allowed' : 'pointer', display: 'inline-flex' }}
                  onClick={(e) => {
                  if (disabled) return;
                  e.preventDefault(); 
                  toggleCategory(option.id, e);
                }}
                >
                  {expandedCategories[option.id] ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </span>
              )}
              
              {/* If no children, add appropriate spacing */}
              {!option.hasChildren && <span style={{ width: '20px', display: 'inline-block' }}></span>}
              
              {/* Category name */}
              {/* <span 
                style={{ cursor: 'pointer' }}
                onClick={() => handleSelectCategory(option)}
              >
                {option.name}
              </span> */}
              <span>{option.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

CategoryDropdown.propTypes = {
  categoriesList: PropTypes.shape({
    categories: PropTypes.array,
    id: PropTypes.number
  }),
  isCategory: PropTypes.bool,
  selectedCategory: PropTypes.object,
  setSelectedCategory: PropTypes.func.isRequired,
  excludeCategoryName: PropTypes.string,
  disabled: PropTypes.bool,
};

CategoryDropdown.defaultProps = {
  categoriesList: { categories: [] },
  isCategory: false,
  selectedCategory: null,
  excludeCategoryName: null,
  disabled: false,
};

export default CategoryDropdown;